import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Flexer, Spacer } from '@ontruck/orc';
import { useLocalStorage } from 'utils/useLocalStorage';

import LogItem from './LogItem';
import ManualLog from './creation/ManualLog';
import Manualtask from './creation/ManualTask';
import ListHeader from './ListHeader';
import LogsLoader from './Loader';

import { StyledList, StyledContainer } from './styled';

import {
  getManuallyLogsCreated,
  isManualLogOpened,
  isManualTaskOpened,
  getIsLoading,
  getIsLoadingNextPage,
  getLogs,
  getTotalLogs,
  getNextLogsPageLink,
} from 'redux/modules/log/selectors';
import { setManualTaskOpened } from 'redux/modules/log/duck';
import { fetchLogs, fetchNextLogsPage, clearLogs } from 'redux/modules/log/effects';
import { addManualEntryLog as onCreateJourneyLogClick } from 'redux/modules/job-detail/effects';

const t = {
  no_logs: 'The log is empty',
};

const getEntityIdObject = ({ journeyId, itineraryId, shipperOrderId }) => {
  if (itineraryId) {
    return { itineraryId };
  }
  if (journeyId) {
    return { journeyId };
  }
  return { shipperOrderId };
};

const Logs = ({
  intl,
  reduced,
  showAuthor = true,
  showTitle = true,
  showAddLog = true,
  onCreateLogClick,
  shipperOrdersSkin = {},
  journeyId,
  shipperOrderId,
  itineraryId,
  ...props
}) => {
  const dispatch = useDispatch();

  const [hideSomeLogs, setHideSomeLogs] = useLocalStorage('logs_hidden', true);
  const logs = useSelector(getLogs);
  const totalLogs = useSelector(getTotalLogs);
  const loading = useSelector(getIsLoading);
  const loadingNextPage = useSelector(getIsLoadingNextPage);
  const hasNextPage = useSelector(getNextLogsPageLink);
  const manuallyLogsCreated = useSelector(getManuallyLogsCreated);
  const manualLogOpened = useSelector(isManualLogOpened);
  const manualTaskOpened = useSelector(isManualTaskOpened);

  const handleLoadMore = useCallback(() => {
    dispatch(fetchNextLogsPage({ hideSomeLogs }));
  }, [hideSomeLogs, dispatch]);

  const handleOpenManualTask = useCallback(() => {
    dispatch(setManualTaskOpened(true));
  }, [dispatch]);

  const handleCreateJourneyLogClick = useCallback(() => {
    dispatch(onCreateJourneyLogClick());
  }, [dispatch]);

  const infiniteRef = useInfiniteScroll({
    hasNextPage,
    loading: loadingNextPage,
    onLoadMore: handleLoadMore,
    threshold: 0,
  });

  const entityObject = useMemo(
    () => getEntityIdObject({ itineraryId, journeyId, shipperOrderId }),
    [itineraryId, journeyId, shipperOrderId]
  );

  useEffect(() => {
    dispatch(clearLogs());
    dispatch(fetchLogs({ hideSomeLogs, ...entityObject }));
    return () => {
      dispatch(clearLogs());
    };
  }, [dispatch, entityObject, hideSomeLogs]);

  return (
    <StyledContainer reduced={reduced} {...props}>
      {showTitle && (
        <ListHeader
          hideSomeLogs={hideSomeLogs}
          setHideSomeLogs={setHideSomeLogs}
          logs={logs}
          journeyId={journeyId}
          showAddLog={showAddLog}
          onCreateLogClick={onCreateLogClick}
          onCreateJourneyLogClick={handleCreateJourneyLogClick}
          handleOpenManualTask={handleOpenManualTask}
          totalLogs={totalLogs}
        />
      )}
      <StyledList data-testid="LogsList" overflow={reduced}>
        {!!manualLogOpened && (
          <ManualLog
            shipperOrderId={shipperOrderId}
            shipperOrdersSkin={shipperOrdersSkin}
            journeyId={journeyId}
          />
        )}
        {!!manualTaskOpened && (
          <Manualtask journeyId={journeyId} shipperOrdersSkin={shipperOrdersSkin} />
        )}

        {!!manuallyLogsCreated.length &&
          manuallyLogsCreated?.map((log, key) => (
            <LogItem key={key} shipperOrdersSkin={shipperOrdersSkin} {...log} />
          ))}
        {!!logs.length && (
          <div ref={infiniteRef}>
            {logs.map((log) => (
              <LogItem key={log.id} shipperOrdersSkin={shipperOrdersSkin} {...log} />
            ))}
          </div>
        )}
      </StyledList>
      {loading && (
        <Spacer pTop="S" pBottom="S">
          <LogsLoader />
          <LogsLoader />
          <LogsLoader />
          <LogsLoader />
        </Spacer>
      )}
      {!loading && !logs.length && !manualLogOpened && !manuallyLogsCreated?.length && (
        <Flexer.Parent justifyContent="center" mTop="S" mBottom="S">
          {t.no_logs}
        </Flexer.Parent>
      )}
    </StyledContainer>
  );
};

export default Logs;
